/**
 * KlFollowupDateEditor — Inline-Date-Picker für projects.kl_followup_date
 * (KL-Wiedervorlage „Wieder fällig am"; KEINE Reminder-Mails). Geteilt zwischen
 * Staffing-neu-Liste, Projektdetail-Übergaben und Handover-Page (Staffing neu, Issue #1).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';
import { CommittedDateInput } from './committed-date-input.js';

export function KlFollowupDateEditor({
  projectId,
  value,
  canEdit,
  onSaved,
}: {
  projectId: string;
  value: string | null | undefined;
  canEdit: boolean;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mut = useMutation({
    mutationFn: (next: string | null) => api.patchProject(projectId, { kl_followup_date: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['v2-kl-staffing'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-project', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['v2-handover-projects'] });
      toast.success(t('v2.klfollowup.saved', 'Wiedervorlage gespeichert'));
      onSaved?.();
    },
    onError: (err) =>
      toast.error(
        t('v2.klfollowup.failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <div className="flex items-center gap-2">
      <CommittedDateInput
        value={value}
        onCommit={(next) => mut.mutate(next)}
        canEdit={canEdit}
        busy={mut.isPending}
      />
      {value && canEdit && (
        <button
          type="button"
          onClick={() => mut.mutate(null)}
          disabled={mut.isPending}
          title={t('v2.klfollowup.clear', 'Datum löschen')}
          className="text-[11px] px-1.5 py-0.5 rounded"
          style={{
            background: 'var(--surface-2)',
            color: 'var(--ink-3)',
            border: '1px solid var(--line-soft)',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}
